import { Suspense } from 'react';
import { Route } from 'react-router';
import { LoadingScreen } from '@/components/base';
import { ActionType, LoadingLogData } from '@/interfaces/logging';
import RoleGuard from '@/router/appRoots/RoleGuard';
import type { RouteData } from '@/router/routes';
import Error from '@/routes/Error';

/**
 * Owns `toRouteElement` — separate from `@/router/utils` because it renders
 * `RoleGuard`, which reaches `useProtectedRoute` → `routes.tsx`. Keeping it out
 * of `utils` (imported by `routes.tsx` for `lazyWithReload`) breaks the
 * lazyWithReload circular import that crashed static importers of route
 * registries (Storybook stories, page specs).
 */
const toRouteElement = (route: RouteData): React.ReactElement => {
  const RouteComponent = route.component;
  const ErrorComponent = route.errorComponent || Error;
  const LayoutComponent = route.layoutComponent || undefined;

  const loadingData: LoadingLogData = {
    action: ActionType.OnPageLoad,
    component: 'toRouteElement',
    description: `Fallback to Suspense due to lazyLoading: ${RouteComponent.name}`,
  };

  const layoutLoadingData: LoadingLogData = {
    action: ActionType.OnPageLoad,
    component: 'toRouteElement',
    description: `Loading layout component: ${LayoutComponent?.name || 'Unknown'}`,
    context: { routePath: route.path, hasLayout: Boolean(LayoutComponent) },
  };

  const GuardedRouteComponent = () =>
    route.roles ? (
      <RoleGuard roles={route.roles}>
        <Suspense fallback={<LoadingScreen loadingLogData={loadingData} />}>
          <RouteComponent />
        </Suspense>
      </RoleGuard>
    ) : (
      <Suspense fallback={<LoadingScreen loadingLogData={loadingData} />}>
        <RouteComponent />
      </Suspense>
    );

  return (
    <Route
      key={route.key || route.path}
      path={route.path}
      element={
        LayoutComponent ? (
          <Suspense fallback={<LoadingScreen loadingLogData={layoutLoadingData} />}>
            <LayoutComponent {...route.layoutComponentProps}>
              <GuardedRouteComponent />
            </LayoutComponent>
          </Suspense>
        ) : (
          <Suspense fallback={<LoadingScreen loadingLogData={layoutLoadingData} />}>
            <GuardedRouteComponent />
          </Suspense>
        )
      }
      errorElement={<ErrorComponent />}
    >
      {route.children?.map(toRouteElement)}
    </Route>
  );
};

export { toRouteElement };
